// Whether the company mirrors ServiceTitan (st-mirror-and-cutover §3): the office's one read of it.
// While it mirrors, ServiceTitan owns its records, so a screen hides or disables each write on them
// and says this line up front, instead of the server refusing it after the click. Set from
// /auth/session at sign-in and again when the live stream says the mode moved; a mirrored refusal
// from any call sets it too, for a screen opened before the switch.
import { useSyncExternalStore } from 'react';

/** What a screen says where a ServiceTitan-owned write would be (the lead's words). */
export const MIRROR_LINE = 'ServiceTitan owns this until cutover: change it there.';

let mirroring = false;
const listeners = new Set<() => void>();

export function setCompanyMode(mode: 'live' | 'mirror' | undefined | null): void {
  const next = mode === 'mirror';
  if (next === mirroring) return;
  mirroring = next;
  for (const l of listeners) l();
}

export const isMirroring = () => mirroring;

/** True while the company mirrors ServiceTitan: hide or disable what ServiceTitan owns, with MIRROR_LINE. */
export function useMirror(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => mirroring,
  );
}

/**
 * An event on the live stream that moves the company's mode: the session is read again on it, so a
 * screen left open through the switch shows what it may change without a reload (QA-641). Each fires
 * once the control plane and the company agree (st-mirror §3.4, §4): entering the mirror, going live,
 * and back to the mirror within the window. goLive's own tenant event (migration.golive_committed)
 * comes before the control plane flips, so it isn't one.
 */
export const MODE_EVENTS = /^migration\.(mirror_entered|went_live|back_to_mirror)$/;
